import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { SidebarModule } from 'primeng/sidebar';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, finalize, map, skip, takeUntil } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { trimTrailingSlashes } from './utilities/utils/security.util';
import { ForgotPasswordComponent } from './features-modules/auth/forgot-password/forgot-password.component';
import { LoginComponent } from './features-modules/auth/login/login.component';
import { SignupComponent } from './features-modules/auth/signup/signup.component';
import { FooterComponent } from './features-modules/footer/footer.component';
import { HeaderComponent } from './features-modules/header/header.component';
import { NewRegistrationViewport } from './features-modules/new-registration/services/new-registration-viewport.service';
import { ProfileStatusPopupComponent } from './shared/profile-status-popup/profile-status-popup.component';
import { PaymentResponsePopupComponent } from './shared/payment-response-popup/payment-response-popup.component';
import { Template1SidebarComponent } from './shared/template1-sidebar/template1-sidebar.component';
import { TemplateService } from './utilities/services/template/template.service';
import { TextScaleService } from './utilities/services/text-scale/text-scale.service';
import { CommonServiceService } from './utilities/services/common-service/common-service.service';
import { InvoiceService } from './utilities/services/invoice-service/invoice.service';
import { EncryptDecryptService } from './utilities/services/EncrptyDecrypt/encrypt-decrypt.service';
import { RoleServiceService } from './utilities/services/role-service/role-service.service';
import { SurfaceLabelService } from './utilities/services/surface-label.service';
import { ThemeServiceService } from './utilities/services/theme-service.service';
import { TitleService } from './utilities/services/title-service/title.service';
import { AnalyticsService } from './utilities/services/analytics/analytics.service';
import { AuthServiceService } from './utilities/services/auth-service/auth-service.service';
import {
  computeDashboardProfileCompletionPercent,
  persistProfileCompletionLocalStorage,
} from './utilities/profile-completion.util';
import { HttpRes } from './models/common/http.model';
import { isCourtBasedSportEnv } from './shared/sport-terminology';
import {
  NON_PLAYER_PROFILE_ROUTE,
  PLAYER_PROFILE_ROUTE,
} from './utilities/profile-route/profile-route.constants';
import { shouldOpenLoginAfterLogout } from './utilities/auth-session.util';
import { syncSentryUserFromStoredToken } from './utilities/monitoring/sentry/sentry-user.util';
import {
  persistManualUiLanguage,
  resolveUiLanguage,
} from './utilities/utils/client-language.util';
import { TabViewModule } from 'primeng/tabview';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    LoginComponent,
    SidebarModule,
    DialogModule,
    ForgotPasswordComponent,
    SignupComponent,
    TranslateModule,
    HeaderComponent,
    FooterComponent,
    ProfileStatusPopupComponent,
    PaymentResponsePopupComponent,
    Template1SidebarComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  /** After "Edit Profile" from completion popup; skip re-opening on my-profile until user leaves that route */
  private static readonly PROFILE_COMPLETION_POPUP_SUPPRESS_KEY =
    'suppress_profile_completion_popup';
  private static readonly IFRAME_SCALE_FACTOR = 0.6;

  title = 'ettu-web';
  sidebarVisible: boolean = false;

  isDialogVisible: boolean = false;
  is_login: boolean = false;
  is_forgot_password: boolean = false;
  is_signup: boolean = false;
  showProfileStatusPopup = false;
  profileStatusPopupType:
    | 'complete_profile'
    | 'verification_pending'
    | 'verification_rejected' = 'complete_profile';
  profileProgress = 0;
  private loginSubscription?: Subscription;
  private forgotPasswordSubscription?: Subscription;
  private signupSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private profileGateSubscription?: Subscription;
  private lastVerificationCheckPath = '';
  private readonly appDestroy$ = new Subject<void>();

  /** Stripe return: `order_id` + `is_success` on any path (global popup). */
  showPaymentPopup = false;
  paymentSuccess = false;
  paymentTransactionId = '';
  paymentOrderDetails: any = null;
  paymentPopupEventTitle = '';
  readonly paymentCurrency = environment.currency || '';
  private processingPaymentReturn = false;
  private isIframeScaled = false;
  private disableIframeClicks = false;

  /**
   * When true, global header/footer are hidden (e.g. order payment redirect).
   */
  hideHeaderAndFooter = false;
  hideFooter = false;

  /**
   * On phones the global header is hidden only inside an event's registration shell (`/new-registration/:eventId/…`,
   * which has its own back arrow); the events list and history keep it.
   */
  get hideHeaderForNewRegistration(): boolean {
    if (!this.newRegistrationViewport.isMobile()) {
      return false;
    }
    const [, root, eventId] = this.router.url.split(/[?#]/)[0].split('/');
    return root === 'new-registration' && !!eventId && eventId !== 'history';
  }

  /**
   * True when the tenant renders the OTT layout (template 1): the top header is
   * replaced by the floating sidebar rail and content is inset behind it.
   */
  get isTemplate1(): boolean {
    return this.templateService.isTemplate1;
  }

  @HostListener('window:resize')
  onResize() {
    this.handleViewportChange();
  }

  @HostListener('window:orientationchange')
  onOrientationChange() {
    setTimeout(() => this.handleViewportChange(), 150);
  }
  innerHeight: any;
  screenWidth: any;
  private orientationChangeHandler?: () => void;

  private handleViewportChange() {
    if (globalThis.window !== undefined) {
      this.innerHeight = globalThis.window.innerHeight;
      this.screenWidth = globalThis.window.innerWidth;
    }
  }
  private removeClickBlocker?: () => void;
  constructor(
    private readonly commonService: CommonServiceService,
    private readonly titleService: TitleService,
    private readonly translateService: TranslateService,
    private readonly router: Router,
    private readonly encryptDecryptService: EncryptDecryptService,
    private readonly surfaceLabelService: SurfaceLabelService,
    private readonly themeService: ThemeServiceService,
    private readonly roleService: RoleServiceService,
    private readonly invoiceService: InvoiceService,
    private analytics: AnalyticsService,
    private authService: AuthServiceService,
    private readonly templateService: TemplateService,
    private readonly textScaleService: TextScaleService,
    private readonly newRegistrationViewport: NewRegistrationViewport,
  ) {
    // Toasts and CDK overlays render into containers appended to <body>,
    // outside `.t1-app`, so the template-1 skin needs a hook on <body> itself.
    if (this.isTemplate1) {
      document.body.classList.add('t1-template');
    }

    // Restore the viewer's saved reader text size before the first paint.
    this.textScaleService.restore();

    this.translateService.setFallbackLang('en');

    const availableLanguages = this.getAvailableLanguages();
    const defaultLang = resolveUiLanguage();
    let savedLang = defaultLang;
    if (environment.tenant_Name === 'sbtf' || environment.tenant_Name === 'sbtf_test') {
      if (savedLang === 'sv') {
        savedLang = 'swedan';
      }
    }

    const isLangAvailable = availableLanguages.includes(savedLang);
    const languageToUse = isLangAvailable ? savedLang : 'en';

    this.translateService.use(languageToUse);

    // Initialize surface label service with sport_id from environment
    if (environment.sport_id !== undefined) {
      this.surfaceLabelService.setSportId(environment.sport_id);
    }
    let themeName = environment.theme_name + '-custom-theme';

    this.themeService.setCustomTheme(themeName);
    // }
  }

  get setWidth(): string {
    const tabletWidth = 1024;
    const mobileWidth = 600;
    if (this.screenWidth < mobileWidth) {
      return '100vw';
    }
    if (this.screenWidth < tabletWidth) {
      return '75vw';
    }
    return '28vw';
  }
  private getAvailableLanguages(): string[] {
    // Define limited languages for sport_id 2
    const limitedLanguages = ['en', 'de', 'fr', 'it'];

    // Define all available languages for other sport_ids
    const allLanguages = [
      'en',
      'norway',
      'swedan',
      'sv',
      'spain',
      'cs',
      'da',
      'de',
      'fr',
      'it',
    ];

    // Court-based sports (Padel, Tennis): same limited set as historical Padel build
    return isCourtBasedSportEnv() ? limitedLanguages : allLanguages;
  }

  ngAfterViewInit(): void {
    window.parent.postMessage(
      { type: 'IFRAME_READY' },
      '*',
    );
  }

  ngOnInit(): void {
    const handler = (event: Event) => {
      if (this.disableIframeClicks) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation(); // 🔥 critical
      }
    };

    document.addEventListener('click', handler, true); // ✅ capture phase

    this.removeClickBlocker = () => {
      document.removeEventListener('click', handler, true);
    };
    this.handleViewportChange();
    // Backup: matchMedia orientation (iOS 13+ where orientationchange may not fire)
    if (globalThis.window?.matchMedia) {
      const mql = globalThis.window.matchMedia('(orientation: portrait)');
      this.orientationChangeHandler = () => this.handleViewportChange();
      mql.addEventListener?.('change', this.orientationChangeHandler);
    }
    this.analytics.init({ tenantName: environment.tenant_Name });
    syncSentryUserFromStoredToken();
    this.titleService.setTitle();
    this.titleService.setIcon();
    this.titleService.setOgTags();
    this.titleService.loadAds();
    this.loginSubscription = this.commonService.login_subject.subscribe(
      (res: any) => {
        this.is_login = res;
        this.isDialogVisible = res;
        this.is_signup = false;
        this.is_forgot_password = false;
      },
    );
    // skip(1) skips the BehaviorSubject's initial false emission on subscription.
    // Without it, is_login = !false = true on page load, causing LoginComponent to
    // render inside the hidden sidebar and fire a duplicate getFooterConfig() API call.
    this.forgotPasswordSubscription =
      this.commonService.forgot_password_subject.pipe(skip(1)).subscribe((res: any) => {
        this.is_forgot_password = res;
        this.is_login = !res;
        this.isDialogVisible = res;
        this.is_signup = false;
      });
    this.signupSubscription = this.commonService.signup_subject.pipe(skip(1)).subscribe(
      (res: any) => {
        this.is_signup = res;
        this.isDialogVisible = res;
        this.is_forgot_password = false;
        this.is_login = !res;
      },
    );
    if (shouldOpenLoginAfterLogout()) {
      this.commonService.login_subject.next(true);
      this.commonService.is_login_success.next(false);
    }
    this.handleTokenNavigation();
    this.persistRoleInformation();
    this.checkVerificationStatus();

    this.routerSubscription = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.analytics.trackPageView(event.urlAfterRedirects);
        this.syncHeaderFooterVisibility();
        const path = event.urlAfterRedirects.split('?')[0];
        if (path !== this.lastVerificationCheckPath) {
          this.lastVerificationCheckPath = path;
          this.checkVerificationStatus();
        }
        this.checkPaymentRedirectQueryParams();
      });

    this.syncHeaderFooterVisibility();
    this.checkPaymentRedirectQueryParams();

    // Membership (and similar) gates: show popup without calling player/info — avoid extra API from shop flow
    this.profileGateSubscription =
      this.commonService.profileGatePopupRequest$.subscribe((type) => {
        if (type === 'complete_profile') {
          this.profileStatusPopupType = 'complete_profile';
          const cached = Number.parseInt(
            localStorage.getItem('profile_completion_percent') || '0',
            10,
          );
          this.profileProgress = Number.isFinite(cached)
            ? Math.min(100, Math.max(0, cached))
            : 0;
          this.showProfileStatusPopup = true;
          this.fetchAndUpdateProfileProgress();
        } else {
          this.profileStatusPopupType = type;
          this.showProfileStatusPopup = true;
        }
      });
  }

  private checkVerificationStatus(): void {
    if (!this.hasBrowserContext()) return;
    const token =
      localStorage.getItem('webToken') || localStorage.getItem('token');
    if (!token) return;

    const url = this.router.url.split('?')[0];
    const isMyProfilePage = url === this.profileGateRoute;
    if (!isMyProfilePage) {
      this.clearProfileCompletionPopupSuppressed();
      this.showProfileStatusPopup = false;
      return;
    }

    const isProfileCompleted = localStorage.getItem('is_profile_completed');
    const isVerified = localStorage.getItem('is_verified');

    if (isProfileCompleted === null || isVerified === null) {
      this.showProfileStatusPopup = false;
      return;
    }

    if (isVerified === 'false') {
      if (isProfileCompleted === 'true') {
        this.fetchAndUpdateVerificationStatus();
        return;
      }
      this.profileStatusPopupType = 'complete_profile';
      this.fetchAndUpdateProfileProgress(this.isProfileCompletionPopupSuppressed());
      return;
    }

    if (isProfileCompleted === 'false') {
      this.profileStatusPopupType = 'complete_profile';
      if (this.isProfileCompletionPopupSuppressed()) {
        this.showProfileStatusPopup = false;
        this.fetchAndUpdateProfileProgress(true);
        return;
      }
      this.fetchAndUpdateProfileProgress();
      return;
    }

    this.showProfileStatusPopup = false;
  }

  /**
   * Toggles main chrome for routes that should render full-viewport content.
   */
  private syncHeaderFooterVisibility(): void {
    const path = this.router.url.split('?')[0];
    this.hideHeaderAndFooter = path === '/orders/payment';
    this.hideFooter =
      path === '/registration' ||
      path.startsWith('/events/register/') ||
      path === '/club-registration' ||
      path.startsWith('/club-registration/') ||
      path === '/new-registration' ||
      path.startsWith('/new-registration/') ||
      this.isRegistrationHistoryPath(path) ||
      this.isOrderHistoryFooterHiddenPath(path) ||
      this.isInsideEventPath(path);
  }

  private isClubRegistrationPath(path: string): boolean {
    return path === '/club-registration' || path.startsWith('/club-registration/');
  }

  /**
   * Event routes that render inside a specific event (e.g. /events/2138/168/1/5/5)
   * hide the global footer, while the event listing (/events/:id) keeps it.
   */
  private isInsideEventPath(path: string): boolean {
    const normalized = path.replace(/\/+$/, '') || '/';
    const parts = normalized.split('/').filter(Boolean);
    return parts[0] === 'events' && parts.length > 2;
  }

  /**
   * Registration module routes that hide the global footer (history, delegation, travel, accommodation, etc.).
   */
  private isRegistrationHistoryPath(path: string): boolean {
    const normalized = trimTrailingSlashes(path);
    const parts = normalized.split('/').filter(Boolean);
    if (parts[0] !== 'registration') {
      return false;
    }

    const reservedSecond = new Set([
      'send-registration',
      'official-registration',
      'travel',
      'hotel',
      'version-history',
      'my-registration',
      'accommodation',
      'order-history',
    ]);

    if (parts.length === 3) {
      if (reservedSecond.has(parts[1])) {
        return false;
      }
      return true;
    }

    if (parts.length === 4 && parts[3] === 'history') {
      if (reservedSecond.has(parts[1])) {
        return false;
      }
      return true;
    }

    if (
      parts[1] === 'accommodation' ||
      parts[1] === 'travel' ||
      parts[1] === 'order-history'
    ) {
      return true;
    }

    return false;
  }

  /**
   * Order-history detail routes that hide the global footer (accommodation, travel).
   */
  private isOrderHistoryFooterHiddenPath(path: string): boolean {
    const normalized = trimTrailingSlashes(path);
    const parts = normalized.split('/').filter(Boolean);
    if (parts[0] !== 'order-history') {
      return false;
    }

    const hiddenSecondSegments = new Set(['accomodation', 'accommodation', 'travel']);
    return parts.length >= 2 && hiddenSecondSegments.has(parts[1]);
  }

  closeDialog() {
    this.isDialogVisible = false;
    if (globalThis.window !== undefined) {
      sessionStorage.removeItem('redirectAfterLogin');
      sessionStorage.removeItem('redirectSource');
    }
  }

  /** Logged-in role, read from the token. */
  private isPlayerRole(): boolean {
    const token =
      localStorage.getItem('webToken') || localStorage.getItem('token');
    try {
      const payload = JSON.parse(atob(token!.split('.')[1]));
      return String(payload?.role_name ?? '').toLowerCase() === 'player';
    } catch {
      return false;
    }
  }

  /** Page that hosts the profile-completion popup for the logged-in role. */
  private get profileGateRoute(): string {
    return this.isPlayerRole()
      ? PLAYER_PROFILE_ROUTE
      : NON_PLAYER_PROFILE_ROUTE;
  }

  private fetchProfileInfo(refresh = false): Observable<HttpRes> {
    if (this.isPlayerRole()) {
      return this.commonService.getPlayerInfo(refresh);
    }
    const userRoleId = Number(
      this.encryptDecryptService.decryptUsingAES256(
        localStorage.getItem('user_role_id'),
      ),
    );
    return this.roleService.getUserRoleById(userRoleId).pipe(
      map((res: any) => ({
        ...res,
        data: {
          ...res?.data,
          user_role_fields: (res?.data?.user_role_fields ?? []).map((f: any) => ({
            value: f?.value,
            is_filled: f?.is_filled,
            role_field: {
              is_required: f?.is_required,
              is_deleted: f?.is_deleted,
              is_publish: f?.is_publish,
              field: { type: f?.type },
            },
          })),
        },
      })),
    );
  }

  private fetchAndUpdateVerificationStatus(): void {
    this.fetchProfileInfo(true).subscribe({
      next: (res) => {
        const data = res?.data;
        const status = (data?.verification_status || '').toUpperCase();
        if (status === 'ACCEPTED') {
          localStorage.setItem('is_verified', 'true');
          this.applyApiProfileFlags(data);
          return;
        }
        if (status === 'REJECTED') {
          localStorage.setItem('is_verified', 'false');
          localStorage.setItem('verification_status', 'REJECTED');
          this.profileStatusPopupType = 'verification_rejected';
        } else {
          localStorage.setItem('is_verified', 'false');
          localStorage.setItem('verification_status', 'PENDING');
          this.profileStatusPopupType = 'verification_pending';
        }
        this.applyApiProfileFlags(data);
        this.showProfileStatusPopup = true;
      },
      error: () => {
        this.profileStatusPopupType = 'verification_pending';
        this.showProfileStatusPopup = true;
      },
    });
  }


  /** `is_profile_completed` / `is_verified` from `get_user_role` — other roles only. */
  private applyApiProfileFlags(data?: any): void {
    if (this.isPlayerRole()) return;
    if (typeof data?.is_profile_completed === 'boolean') {
      localStorage.setItem(
        'is_profile_completed',
        String(data.is_profile_completed),
      );
    }
    if (typeof data?.is_verified === 'boolean') {
      localStorage.setItem('is_verified', String(data.is_verified));
    }
  }

  private applyProfileProgressFromPercent(percent: number, data?: any): void {
    const apiCompleted =
      !this.isPlayerRole() && data?.is_profile_completed === true;
    this.profileProgress = apiCompleted ? 100 : percent;
    persistProfileCompletionLocalStorage(this.profileProgress);
    this.applyApiProfileFlags(data);
  }

  private finishProfileProgressPopupFlow(
    silent: boolean,
    status: string,
  ): void {
    const isVerified = localStorage.getItem('is_verified');
    const profileComplete =
      this.profileProgress === 100 ||
      localStorage.getItem('is_profile_completed') === 'true';

    if (isVerified === 'false' && profileComplete) {
      this.profileStatusPopupType =
        status === 'REJECTED'
          ? 'verification_rejected'
          : 'verification_pending';
      this.showProfileStatusPopup = true;
      return;
    }

    if (silent) {
      this.showProfileStatusPopup = false;
      return;
    }

    const gateProfileIncomplete =
      localStorage.getItem('is_profile_completed') === 'false';
    const allowCompleteProfilePopup =
      !this.isProfileCompletionPopupSuppressed();
    if (gateProfileIncomplete || this.profileProgress < 100) {
      this.profileStatusPopupType = 'complete_profile';
      if (allowCompleteProfilePopup) {
        this.showProfileStatusPopup = true;
      }
    }
  }

  /**
   * @param silent When true, refresh progress / localStorage only (no completion popup).
   */
  private fetchAndUpdateProfileProgress(silent = false): void {
    this.fetchProfileInfo().subscribe({
      next: (res) => {
        const data = res?.data;
        const status = (data?.verification_status || '').toUpperCase();
        if (status === 'ACCEPTED') {
          localStorage.setItem('is_verified', 'true');
        } else if (status === 'REJECTED') {
          localStorage.setItem('is_verified', 'false');
          localStorage.setItem('verification_status', 'REJECTED');
        } else {
          localStorage.setItem('is_verified', 'false');
          localStorage.setItem('verification_status', 'PENDING');
        }
        const fields = (data?.user_role_fields ?? []).filter(
          (f: any) => f?.role_field?.is_required === true
        );
        const parents = data?.parents;
        const roleId =
          data?.user_role?.role_id == null
            ? null
            : Number(data.user_role.role_id);
        if (roleId) {
          this.roleService.get_role_parents(roleId).subscribe({
            next: (rpRes: any) => {
              const arr = Array.isArray(rpRes?.data)
                ? rpRes.data
                : rpRes?.data?.data || [];
              const percent = computeDashboardProfileCompletionPercent(
                fields,
                arr,
                parents,
              );
              this.applyProfileProgressFromPercent(percent, data);
              this.finishProfileProgressPopupFlow(silent, status);
            },
            error: () => {
              const percent = computeDashboardProfileCompletionPercent(
                fields,
                [],
                parents,
              );
              this.applyProfileProgressFromPercent(percent, data);
              this.finishProfileProgressPopupFlow(silent, status);
            },
          });
          return;
        }
        const percent = computeDashboardProfileCompletionPercent(
          fields,
          [],
          parents,
        );
        this.applyProfileProgressFromPercent(percent, data);
        this.finishProfileProgressPopupFlow(silent, status);
      },
      error: () => {
        if (silent) {
          this.showProfileStatusPopup = false;
          return;
        }
        this.profileProgress = Number.parseInt(
          localStorage.getItem('profile_completion_percent') || '0',
          10,
        );
        const isVerified = localStorage.getItem('is_verified');
        const gateProfileIncomplete =
          localStorage.getItem('is_profile_completed') === 'false';
        const allowCompleteProfilePopup =
          !this.isProfileCompletionPopupSuppressed();
        if (this.profileProgress === 100 && isVerified === 'false') {
          this.profileStatusPopupType = 'verification_pending';
          this.showProfileStatusPopup = true;
        } else if (gateProfileIncomplete || this.profileProgress < 100) {
          this.profileStatusPopupType = 'complete_profile';
          if (allowCompleteProfilePopup) {
            this.showProfileStatusPopup = true;
          }
        }
      },
    });
  }

  navigateToEditProfile() {
    this.setProfileCompletionPopupSuppressed();
    const url = this.router.url.split('?')[0];
    if (!this.isPlayerRole()) {
      this.router.navigate([NON_PLAYER_PROFILE_ROUTE]);
      return;
    }
    if (url === PLAYER_PROFILE_ROUTE) {
      this.commonService.openEditProfilePopup$.next(true);
    } else {
      this.router.navigate([PLAYER_PROFILE_ROUTE], {
        queryParams: { openEditProfile: 'true' },
      });
    }
  }

  private isProfileCompletionPopupSuppressed(): boolean {
    if (
      globalThis.window === undefined ||
      sessionStorage === undefined
    ) {
      return false;
    }
    return (
      sessionStorage.getItem(
        AppComponent.PROFILE_COMPLETION_POPUP_SUPPRESS_KEY,
      ) === '1'
    );
  }

  private setProfileCompletionPopupSuppressed(): void {
    if (
      globalThis.window !== undefined &&
      sessionStorage !== undefined
    ) {
      sessionStorage.setItem(
        AppComponent.PROFILE_COMPLETION_POPUP_SUPPRESS_KEY,
        '1',
      );
    }
  }

  private clearProfileCompletionPopupSuppressed(): void {
    if (
      globalThis.window !== undefined &&
      sessionStorage !== undefined
    ) {
      sessionStorage.removeItem(
        AppComponent.PROFILE_COMPLETION_POPUP_SUPPRESS_KEY,
      );
    }
  }

  ngOnDestroy(): void {
    this.appDestroy$.next();
    this.appDestroy$.complete();
    this.removeClickBlocker?.();
    this.loginSubscription?.unsubscribe();
    this.forgotPasswordSubscription?.unsubscribe();
    this.signupSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
    this.profileGateSubscription?.unsubscribe();
  }

  /**
   * Any route with `?order_id=&is_success=` (Stripe return) opens the ticket
   * popup; path does not need to be a dedicated success URL.
   */
  private checkPaymentRedirectQueryParams(): void {
    const tree = this.router.parseUrl(this.router.url);
    const q = tree.queryParams;
    /** Partner-invite flow handles return on `event-request-details` (payment first, then `update_pair_request_status`). */
    const finalizePair = q['finalize_pair_accept'];
    if (
      finalizePair === '1' ||
      finalizePair === 1 ||
      String(finalizePair).toLowerCase() === 'true'
    ) {
      return;
    }
    const orderIdRaw = q['order_id'];
    const successRaw = q['is_success'];
    const orderIdStr = Array.isArray(orderIdRaw)
      ? orderIdRaw[0]
      : orderIdRaw;
    const successStr = Array.isArray(successRaw)
      ? successRaw[0]
      : successRaw;
    if (
      orderIdStr === undefined ||
      orderIdStr === null ||
      String(orderIdStr).trim() === ''
    ) {
      return;
    }
    if (successStr === undefined || successStr === null) {
      return;
    }
    if (this.processingPaymentReturn) {
      return;
    }
    const orderId = Number.parseInt(String(orderIdStr).trim(), 10);
    if (Number.isNaN(orderId)) {
      return;
    }
    this.processingPaymentReturn = true;
    /** Stripe sends `true` / `false`; treat only explicit true as success. */
    this.paymentSuccess =
      String(successStr).trim().toLowerCase() === 'true';
    this.invoiceService
      .get_orders({
        pageSize: 10,
        page: 1,
        orderId,
        fetchAll: true,
        serializeType: 'registration',
        orderTypeId: 5,
        includeVerificationCounts: true,
        includeRefunds: true,
      })
      .pipe(
        takeUntil(this.appDestroy$),
        finalize(() => {
          this.processingPaymentReturn = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          const orders = response?.data?.orders;
          const orderDetails =
            Array.isArray(orders) && orders.length > 0 ? orders[0] : null;
          if (!orderDetails) {
            this.paymentOrderDetails = { order_id: orderId };
            this.paymentPopupEventTitle = '';
            this.paymentTransactionId = String(orderId);
            this.showPaymentPopup = true;
            this.clearPaymentReturnQueryParams();
            return;
          }
          this.paymentOrderDetails = {
            ...orderDetails,
            order_id: orderDetails.order_id ?? orderDetails.id,
          };
          this.paymentPopupEventTitle = orderDetails?.event_name || '';
          const payId = orderDetails?.payment_id;
          this.paymentTransactionId =
            payId != null && payId !== ''
              ? String(payId)
              : String(orderId);
          this.showPaymentPopup = true;
          this.clearPaymentReturnQueryParams();
        },
        error: () => {
          /** Keep failure/success from URL (`paymentSuccess`); API may 401/404. */
          this.paymentOrderDetails = { order_id: orderId };
          this.paymentPopupEventTitle = '';
          this.paymentTransactionId = String(orderId);
          this.showPaymentPopup = true;
          this.clearPaymentReturnQueryParams();
        },
      });
  }

  private clearPaymentReturnQueryParams(): void {
    this.router.navigate([], {
      queryParams: { order_id: null, is_success: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private pickPaymentPopupCategoryLines(order: any): string[] {
    const fromAgg = this.collectUniqueCategoryNames(
      order?.category_verification_details,
      (c) => c?.category_name,
    );
    if (fromAgg.length > 0) {
      return fromAgg;
    }
    const parts = order?.participants;
    if (!Array.isArray(parts)) {
      return [];
    }
    const fromParts: string[] = [];
    const seen = new Set<string>();
    for (const p of parts) {
      for (const name of this.collectUniqueCategoryNames(
        p?.category_details,
        (c) => c?.category_name,
      )) {
        if (!seen.has(name)) {
          seen.add(name);
          fromParts.push(name);
        }
      }
    }
    return fromParts;
  }

  private collectUniqueCategoryNames(
    items: unknown,
    getName: (item: any) => unknown,
  ): string[] {
    if (!Array.isArray(items)) {
      return [];
    }
    const seen = new Set<string>();
    const out: string[] = [];
    for (const item of items) {
      const name = getName(item);
      if (typeof name === 'string' && name.trim() !== '' && !seen.has(name)) {
        seen.add(name);
        out.push(name);
      }
    }
    return out;
  }

  getPaymentPopupDetailLines(): string[] {
    if (!this.paymentOrderDetails) {
      return [];
    }
    return this.pickPaymentPopupCategoryLines(this.paymentOrderDetails);
  }

  getPaymentPopupFooterPrice(): string {
    const d = this.paymentOrderDetails;
    if (d?.total == null || d?.total === '') {
      return '';
    }
    return `${this.paymentCurrency}${Number(d.total).toFixed(2)}`;
  }

  onPaymentPopupVisibleChange(visible: boolean): void {
    this.showPaymentPopup = visible;
    if (!visible) {
      this.commonService.paymentReturnPopupDismissed$.next();
    }
  }

  onPaymentPopupViewInvoice(): void {
    this.showPaymentPopup = false;
    this.router.navigate(['/order-history/invoice'], {
      queryParams: { tab: 'membership', subTab: 'invoices' },
    });
  }

  onPaymentPopupViewOrderHistory(): void {
    this.showPaymentPopup = false;
    this.navigateToRegistrationHistoryFromPaymentOrder();
  }

  /**
   * Uses `get_orders` row: `event_id` / `meta.event_id`, `meta.participant_type_id`,
   * and syncs sessionStorage so `RegistrationHistoryComponent` picks up context.
   */
  private navigateToRegistrationHistoryFromPaymentOrder(): void {
    const d = this.paymentOrderDetails;
    const rawEvent = d?.event_id ?? d?.meta?.event_id ?? null;
    if (rawEvent == null || rawEvent === '') {
      this.router.navigate(['/registration/my-registration']);
      return;
    }
    const eventIdStr = String(rawEvent);
    const fromMeta = d?.meta?.participant_type_id;
    const parsed =
      fromMeta != null && fromMeta !== '' ? Number(fromMeta) : Number.NaN;
    const participantId =
      Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    if (
      globalThis.window?.sessionStorage
    ) {
      sessionStorage.setItem('event_id', eventIdStr);
      sessionStorage.setItem(
        'selectedTournamentName',
        String(d?.event_name ?? ''),
      );
    }
    this.router.navigate([
      '/registration',
      eventIdStr,
      String(participantId),
      'history',
    ]);
  }

  onPaymentPopupRetry(): void {
    const d = this.paymentOrderDetails;
    const oid = Number(d?.order_id ?? d?.id);
    this.showPaymentPopup = false;
    if (!Number.isNaN(oid)) {
      this.commonService.paymentReturnPopupRetry$.next(oid);
    }
  }

  show_login() {
    if (this.is_login) {
      return this.translateService.instant('login');
    } else if (this.is_forgot_password) {
      return this.translateService.instant('forgot_password');
    } else {
      return this.translateService.instant('signUp');
    }
  }

  changeLanguage(lang: string) {
    this.translateService.use(lang);
    persistManualUiLanguage(lang);
  }
  private handleTokenNavigation(): void {
    if (!this.hasBrowserContext()) {
      return;
    }

    const code = new URLSearchParams(globalThis.window?.location?.search ?? '').get('code');
    if (code) {
      this.authService.exchangeWebHandoffCode(code)
        .pipe(takeUntil(this.appDestroy$))
        .subscribe({
          next: (res: any) => {
            const token = res?.data?.data?.access_token;
            if (token) {
              localStorage.setItem('token', token);
              localStorage.setItem('webToken', token);
              globalThis.window?.location?.replace('/home');
            } else {
              this.router.navigate(['/login']);
            }
          },
          error: () => this.router.navigate(['/login'])
        });
      return;
    }

    const path = this.getRawPathSegments();
    const pathname = globalThis.window.location.pathname;

    if (pathname.includes('/membership')) {
      const membershipToken = pathname.slice('/membership'.length);
      if (membershipToken && this.looksLikeJwt(membershipToken)) {
        localStorage.setItem('token', membershipToken);
        localStorage.setItem('webToken', membershipToken);
        // Only the SSO token-handoff (/membership/<jwt>) collapses to /membership;
        // real sub-routes like /membership/payment-success must be left to the router.
        this.router.navigate(['/membership']);
      }
      return;
    }

    let token: string | null = null;

    const dummyIndex = path.indexOf('dummy');
    if (dummyIndex !== -1 && path[dummyIndex + 1]) {
      token = path[dummyIndex + 1];
    } else if (path.length === 1 && this.looksLikeJwt(path[0])) {
      token = path[0];
    }
    if (token) {
      localStorage.setItem('token', token);
      localStorage.setItem('webToken', token);
      this.router.navigate(['/home']);
    }
  }

  private persistRoleInformation(): void {
    if (!this.hasBrowserContext()) {
      return;
    }

    const token = localStorage.getItem('webToken');
    if (!token) {
      return;
    }

    try {
      const responseData = JSON.parse(atob(token.split('.')[1]));
      localStorage.setItem(
        'role_id',
        this.encryptDecryptService.encryptUsingAES256(
          responseData.role_id.toString(),
        ),
      );
      localStorage.setItem(
        'user_role_id',
        this.encryptDecryptService.encryptUsingAES256(
          responseData.user_role_id.toString(),
        ),
      );
    } catch (error) {
      console.error('Failed to persist role metadata from token', error);
    }
  }

  private hasBrowserContext(): boolean {
    return globalThis.window?.localStorage != null;
  }

  private looksLikeJwt(candidate: string): boolean {
    return candidate.split('.').length === 3;
  }

  private getRawPathSegments(): string[] {
    if (!this.hasBrowserContext()) {
      return [];
    }
    const pathname = globalThis.window.location.pathname || '';
    return pathname.split('/').filter(Boolean);
  }
  @HostListener('document:click', ['$event'])
  handleGlobalClick(event: Event) {
    if (this.disableIframeClicks) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  @HostListener('window:message', ['$event'])
  onWindowMessage(event: MessageEvent): void {
    const data = event.data;
    if (data == null || typeof data !== 'object') return;

    if (data.type === 'DISABLE_CLICKS') {
      this.disableIframeClicks = true;
      return;
    }

    if (data.type === 'ENABLE_CLICKS') {
      this.disableIframeClicks = false;
      return;
    }

    const requestedTransform = data.transform;
    if (requestedTransform === 'scale') {
      this.applyIframeBodyScale(true);
      return;
    }
    if (requestedTransform === 'reset-scale') {
      this.applyIframeBodyScale(false);
      return;
    }

    const requestedTheme = data.theme;
    if (requestedTheme !== 'dark' && requestedTheme !== 'light') return;

    const currentlyDark = this.themeService.getTheme();
    // Only toggle when needed so we don't invert the correct state.
    if (requestedTheme === 'dark' && !currentlyDark) {
      this.themeService.setDarkModeFromIframe('dark');
    } else if (requestedTheme === 'light' && currentlyDark) {
      this.themeService.setDarkModeFromIframe('light');
    }
  }

  private applyIframeBodyScale(enableScale: boolean): void {
    if (globalThis.window === undefined) return;
    const body = document.body;
    if (!body) return;

    if (enableScale) {
      if (this.isIframeScaled) return;
      const factor = AppComponent.IFRAME_SCALE_FACTOR;
      body.style.transform = `scale(${factor})`;
      body.style.transformOrigin = 'top left';
      body.style.width = `${100 / factor}%`;
      body.style.minHeight = `${100 / factor}%`;
      this.isIframeScaled = true;
      return;
    }

    if (!this.isIframeScaled) return;
    body.style.transform = '';
    body.style.transformOrigin = '';
    body.style.width = '';
    body.style.minHeight = '';
    this.isIframeScaled = false;
  }

  toggleTheme() {
    this.themeService.toggleTheme();
  }
}
